import React, { useEffect, useRef, useState, useCallback } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { translateText } from '@/api/subtitles';
import './GeminiContent.less';
import MeetStatus, { IMeetStatus } from './MeetStatus';
import { speakerColor } from './GeminiTranscriptionContent';

interface PostTranslateContentProps {
  isFinish: boolean;
  meetStatus: IMeetStatus;
  subtitleSegmentId: string;
  fileMergeId: string;
  targetLang: string;
  // 对照原文:勾选后在译文下显示原文
  showSource?: boolean;
  // 整场无实时字幕(静音会议):无转录源,直接显示空态
  noSpeech?: boolean;
}

type TransRow = { speaker_id: string; transcription: string; original?: string; begin_time?: number };

// 毫秒 → mm:ss(逐句时间戳展示)
const fmtClock = (ms?: number) => {
  if (ms == null) return '';
  const total = Math.floor(ms / 1000);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// 大会议首次翻译后端异步生成,返回 translate_status=GENERATING;前端按间隔轮询直到 DONE,
// 超过上限仍未完成则报错(可手动重试)。旧后端不返回该字段时按同步结果处理(向后兼容)。
const POLL_INTERVAL_MS = 3000;
// 轮询上限:dev 实测 900+ 句的大会议后端约需 150s 译完,上限留足余量到 240s,
// 避免最大的会议临界超时报错(即便超时,后端已缓存,手动重试即出)。
const POLL_MAX_MS = 240000;

// 精校翻译:基于精校转录的全文翻译。走文本翻译服务(/text/translate),
// 后端优先用带声纹分离的 Gemini 转录做源,支持任意目标语言(切语言即重译),
// 不依赖多模态翻译 job(平台 Doubao 路径不支持 translate)。
const PostTranslateContent: React.FC<PostTranslateContentProps> = ({
  isFinish,
  meetStatus,
  subtitleSegmentId,
  fileMergeId,
  targetLang,
  showSource,
  noSpeech,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<TransRow[] | null>(null);
  const [error, setError] = useState(false);
  const reqIdRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollDeadlineRef = useRef(0);

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // isPoll=true 表示由轮询触发(沿用当前请求标识、不重置 loading/截止时间)
  const fetchTranslation = useCallback(async (isPoll = false) => {
    if (!subtitleSegmentId) return;
    let reqId = reqIdRef.current;
    if (!isPoll) {
      reqId += 1;
      reqIdRef.current = reqId;
      clearPollTimer();
      pollDeadlineRef.current = Date.now() + POLL_MAX_MS;
      setError(false);
      setLoading(true);
    }
    try {
      const res = await translateText({
        user_subtitle_segment_id: subtitleSegmentId,
        target_lang: targetLang,
        file_merge_id: fileMergeId || undefined,
      });
      // 语言快速切换时丢弃过期响应
      if (reqId !== reqIdRef.current) return;
      const { data } = res.data;
      // 后台仍在生成:在截止时间内继续轮询,保持 loading 态
      if (data.translate_status === 'GENERATING') {
        if (Date.now() >= pollDeadlineRef.current) {
          setError(true);
          setLoading(false);
          return;
        }
        pollTimerRef.current = setTimeout(() => fetchTranslation(true), POLL_INTERVAL_MS);
        return;
      }
      setRows(data.generate_transcription_list || []);
      setLoading(false);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setError(true);
      setLoading(false);
    }
  }, [subtitleSegmentId, targetLang, fileMergeId]);

  useEffect(() => {
    if (noSpeech || !isFinish) return undefined;
    fetchTranslation();
    return clearPollTimer;
  }, [isFinish, noSpeech, fetchTranslation]);

  if (!isFinish) {
    return <MeetStatus meetStatus={meetStatus} contentType="transcription" />;
  }

  if (noSpeech || (!loading && !error && rows && rows.length === 0)) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text">{t('meeting.noSpeechContent')}</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <div className="loading-text"><LoadingOutlined />{t('meeting.translating')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gemini-content">
        <div className="loading-container">
          <Button type="link" onClick={() => fetchTranslation()}>{t('ai.retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="gemini-content">
      <div className="content-container">
        <div className="markdown-content-title">{t('meeting.meetingTranslation')}</div>
        <div className="markdown-content">
          {rows?.map((item, key) => (
            <div key={key} className="transcript-row">
              <div className="speaker-column">
                <div className="speaker-avatar" style={{ background: speakerColor(item.speaker_id) }}>
                  {item.speaker_id && item.speaker_id.length > 0
                    ? item.speaker_id.charAt(0).toUpperCase()
                    : '?'}
                </div>
                <span>{item.speaker_id || t('meeting.unknownSpeaker')}</span>
                {item.begin_time != null && <time className="transcript-time">{fmtClock(item.begin_time)}</time>}
              </div>
              <div className="content-column">
                <div className="source-text">{item.transcription}</div>
                {showSource && item.original && (
                  <div className="translate-text">{item.original}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PostTranslateContent;
